import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { APP_QUERY_KEYS } from '@/lib/constants/app';
import { APP_FACTORY_QUERY_KEYS, type FactoryAssetPlatform } from '@/lib/constants/appFactory';
import { BUILD_READINESS_QUERY_KEYS } from '@/lib/constants/buildReadiness';
import type {
  FactoryAssetDecisionReq,
  FactoryBrandKitGenerateReq,
  FactoryBuildRequestReq,
} from '@/lib/types/AppFactory';
import { AppFactoryApi } from './AppFactoryApi';
import { BrandKitUtils } from './BrandKitUtils';
import { AppBuildRequestUtils } from './AppBuildRequestUtils';
import { SubmissionGuideUtils } from './SubmissionGuideUtils';

// Factory reads degrade quietly: when the gateway is disabled or the daemon is
// unreachable the routes error, so retries are pointless — components render
// an "unavailable" state off isError instead.
const FACTORY_QUERY_OPTS = {
  retry: false,
  staleTime: 30_000,
  refetchOnWindowFocus: false,
} as const;

export function useFactoryReleases(enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.releases(selectedCId ?? 0),
    queryFn: () => AppFactoryApi.getReleases(selectedCId!),
    enabled: enabled && selectedCId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

export function useFactoryTasks(enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.tasks(selectedCId ?? 0),
    queryFn: () => AppFactoryApi.getTasks(selectedCId!),
    enabled: enabled && selectedCId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

/** While a build of the app is in flight the list re-reads on this cadence (a build takes minutes). */
const BUILDS_ACTIVE_POLL_MS = 20_000;

export function useFactoryBuilds(appCfgId: number, enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.builds(selectedCId ?? 0, appCfgId),
    queryFn: () => AppFactoryApi.getBuilds(selectedCId!, appCfgId),
    enabled: enabled && selectedCId != null && appCfgId > 0,
    ...FACTORY_QUERY_OPTS,
    // Holder-requested builds (3.34.0): the Status tab follows a queued build
    // to its resting state without a reload; idle lists never poll.
    refetchInterval: (query) =>
      AppBuildRequestUtils.hasActiveBuild(query.state.data?.builds ?? []) ? BUILDS_ACTIVE_POLL_MS : false,
  });
}

/** While the farm (re)prepares a submission — a store-guide refresh (3.34.2) — the list re-reads on this cadence. */
const SUBMISSIONS_PREPARING_POLL_MS = 20_000;

export function useFactorySubmissions(appCfgId: number, enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.submissions(selectedCId ?? 0, appCfgId),
    queryFn: () => AppFactoryApi.getSubmissions(selectedCId!, appCfgId),
    enabled: enabled && selectedCId != null && appCfgId > 0,
    ...FACTORY_QUERY_OPTS,
    // A guide refresh moves the submission to preparing until the farm's
    // re-test lands; follow it to awaiting_customer without a reload.
    refetchInterval: (query) =>
      SubmissionGuideUtils.hasPreparingSubmission(query.state.data?.submissions ?? [])
        ? SUBMISSIONS_PREPARING_POLL_MS
        : false,
  });
}

/**
 * Holder-requested build (3.34.0): the farm's idempotent claim for one
 * platform + release. Every factory read of the account is invalidated on
 * success — the new row lands in the builds list, the tasks feed, and (once
 * its checks pass) the submissions.
 */
export function useRequestBuild(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: FactoryBuildRequestReq) =>
      AppFactoryApi.requestBuild(selectedCId!, appCfgId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.all });
    },
  });
}

/**
 * Click-triggered presigned downloads (Tier 1). A mutation, not a query:
 * the URLs are short-lived, so they are minted fresh per click and never
 * cached.
 */
export function useSubmissionDownloads(appCfgId: number) {
  const { selectedCId } = useAuth();
  return useMutation({
    mutationFn: (submissionId: string) =>
      AppFactoryApi.getSubmissionDownloads(selectedCId!, appCfgId, submissionId),
  });
}

// ── Store accounts (4.2 delegated store accounts) ──

export function useStoreAccounts(enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.storeAccounts(selectedCId ?? 0),
    queryFn: () => AppFactoryApi.getStoreAccounts(selectedCId!),
    enabled: enabled && selectedCId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

export function useCreateStoreAccount() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: Parameters<typeof AppFactoryApi.createStoreAccount>[1]) =>
      AppFactoryApi.createStoreAccount(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_FACTORY_QUERY_KEYS.storeAccounts(selectedCId!),
      });
    },
  });
}

export function useTransitionStoreAccount() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appStoreAccountId,
      from,
      to,
      note,
    }: {
      appStoreAccountId: string;
      from: string;
      to: string;
      note?: string;
    }) => AppFactoryApi.transitionStoreAccount(selectedCId!, appStoreAccountId, { from, to, note }),
    onSuccess: () => {
      // The tasks feed branches its text on the account state too.
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.all });
    },
  });
}

/**
 * Tenant-scoped submission transition (B3): the holder records the portal
 * steps on its own app; the api derives the actor and scopes the id.
 */
export function useTransitionOwnSubmission(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      submissionId,
      from,
      to,
      note,
    }: {
      submissionId: string;
      from: string;
      to: string;
      note?: string;
    }) =>
      AppFactoryApi.transitionOwnSubmission(selectedCId!, appCfgId, submissionId, { from, to, note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.all });
    },
  });
}

// ── Brand kit (asset sets) ──
//
// One platform's approval history and current images per read (3.34.0): the
// factory stages and promotes each platform's set under its own folder, so
// the platform is part of the key and of every call.

export function useFactoryAssetSets(appCfgId: number, platform: FactoryAssetPlatform, enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.assetSets(selectedCId ?? 0, appCfgId, platform),
    queryFn: () => AppFactoryApi.getAssetSets(selectedCId!, appCfgId, platform),
    enabled: enabled && selectedCId != null && appCfgId > 0,
    ...FACTORY_QUERY_OPTS,
  });
}

export function useCreateAssetSet(appCfgId: number, platform: FactoryAssetPlatform) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: Parameters<typeof AppFactoryApi.createAssetSet>[2]) =>
      AppFactoryApi.createAssetSet(selectedCId!, appCfgId, req, platform),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_FACTORY_QUERY_KEYS.assetSets(selectedCId!, appCfgId, platform),
      });
    },
  });
}

/**
 * When a generate or a decision outran the api's wait, the factory is still at
 * it (one render at a time, so it may be queued): the reads are asked again on
 * this schedule until the result shows.
 */
const BRAND_KIT_SLOW_REFETCH_MS = [4_000, 8_000, 14_000, 22_000, 32_000, 45_000] as const;

/**
 * Brand Kit generate (3.42.0): drafts land on every platform it names, so every
 * platform's asset-set read of the app is invalidated, not one. Also on an
 * error — and again a little later when the api gave up waiting (504): the
 * factory renders every TV platform in one request and finishes it even when
 * the api's 15 s wait ran out, so the drafts still arrive.
 */
export function useGenerateBrandKit(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  const refetch = () =>
    queryClient.invalidateQueries({
      queryKey: APP_FACTORY_QUERY_KEYS.assetSetsApp(selectedCId!, appCfgId),
    });
  return useMutation({
    mutationFn: (req: FactoryBrandKitGenerateReq) =>
      AppFactoryApi.generateBrandKit(selectedCId!, appCfgId, req),
    onSettled: (_res, err) => {
      void refetch();
      if (BrandKitUtils.isStillWorking(err)) {
        for (const ms of BRAND_KIT_SLOW_REFETCH_MS) window.setTimeout(() => void refetch(), ms);
      }
    },
  });
}

/**
 * The Background image saved on its own (3.46.0; appfactory ADR-0052). The
 * answer names the picture and the api stores it as the app's pointer before
 * answering, so the app (the switch's card reads the pointer) and every
 * platform's kit read are re-read; the factory finishes a slow render as a
 * Generate does.
 */
export function useSaveHomeImage(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  const refetch = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.assetSetsApp(selectedCId!, appCfgId) }),
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.detail(selectedCId!, appCfgId) }),
      queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.list(selectedCId!) }),
    ]);
  return useMutation({
    mutationFn: (tileArtBase64: string | null) =>
      AppFactoryApi.saveHomeImage(selectedCId!, appCfgId, BrandKitUtils.homeImageRequest(tileArtBase64)),
    onSettled: (_res, err) => {
      void refetch();
      if (BrandKitUtils.isStillWorking(err)) {
        for (const ms of BRAND_KIT_SLOW_REFETCH_MS) window.setTimeout(() => void refetch(), ms);
      }
    },
  });
}

/**
 * A decision may reach every platform's draft (a kit approval), so every
 * platform's read is invalidated. Also on an error: a refused kit approval may
 * still have published the drafts that passed.
 */
export function useDecideAssetSet(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetSetId, ...body }: { assetSetId: string } & FactoryAssetDecisionReq) =>
      AppFactoryApi.decideAssetSet(selectedCId!, appCfgId, assetSetId, body),
    onSettled: (_res, err) => {
      const refetch = () => {
        void queryClient.invalidateQueries({
          queryKey: APP_FACTORY_QUERY_KEYS.assetSetsApp(selectedCId!, appCfgId),
        });
        // An approved set is the images a build is made with: the Readiness card re-asks (3.34.5).
        void queryClient.invalidateQueries({ queryKey: BUILD_READINESS_QUERY_KEYS.app(selectedCId!, appCfgId) });
        // An approved Brand Kit's background image becomes the app's home background
        // (the api stores it on the app): the app is re-read for the switch (3.44.0).
        void queryClient.invalidateQueries({ queryKey: APP_QUERY_KEYS.detail(selectedCId!, appCfgId) });
      };
      refetch();
      // Approve all promotes every platform's files in one request: it can outrun the api's wait too.
      if (BrandKitUtils.isStillWorking(err)) {
        for (const ms of BRAND_KIT_SLOW_REFETCH_MS) window.setTimeout(refetch, ms);
      }
    },
  });
}

// ── Operator surface (server-side allowlist; 403 for everyone else) ──

export function useFactoryDevices(enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.devices(selectedCId ?? 0),
    queryFn: () => AppFactoryApi.getDevices(selectedCId!),
    enabled: enabled && selectedCId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

/**
 * Is the signed-in login a platform operator? There is deliberately no
 * client-side role: the api's operator allowlist is the authority, so this
 * asks an operator-only read (the farm devices, which the console shares) and
 * treats anything but a success as "no" — fail closed, including while it
 * loads or when the factory is unreachable. Logins without media access are
 * never operators, so they do not ask at all.
 */
export function useIsFactoryOperator(): boolean {
  const { authData } = useAuth();
  const devicesQuery = useFactoryDevices(!!authData?.cpuser.permissions.serviceRead);
  return devicesQuery.isSuccess;
}

export function useFactoryActiveSubmissions(enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.activeSubmissions(selectedCId ?? 0),
    queryFn: () => AppFactoryApi.getActiveSubmissions(selectedCId!),
    enabled: enabled && selectedCId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

export function useFactoryCampaigns(enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.campaigns(selectedCId ?? 0),
    queryFn: () => AppFactoryApi.getCampaigns(selectedCId!),
    enabled: enabled && selectedCId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

export function useFactoryCampaignTargets(campaignId: string | null) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.campaignTargets(selectedCId ?? 0, campaignId ?? ''),
    queryFn: () => AppFactoryApi.getCampaignTargets(selectedCId!, campaignId!),
    enabled: selectedCId != null && campaignId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

export function useAdvanceCampaign() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => AppFactoryApi.advanceCampaign(selectedCId!, campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.all });
    },
  });
}

export function useAbortCampaign() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => AppFactoryApi.abortCampaign(selectedCId!, campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.all });
    },
  });
}

export function useTransitionSubmission() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      submissionId,
      from,
      to,
      note,
    }: {
      submissionId: string;
      from: string;
      to: string;
      note?: string;
    }) => AppFactoryApi.transitionSubmission(selectedCId!, submissionId, { from, to, note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.all });
    },
  });
}
